"use client";

import type { BreadcrumbList, ListItem, WithContext } from "schema-dts";
import { useGeo, withDomain } from "@/i18n/routing";
import { safeStringify } from "@/lib/utils/safeStringify";
import type { BreadcrumbItem } from "./index";

interface BreadcrumbsSchemaProps {
	items: BreadcrumbItem[];
}

const buildSchema = (
	items: BreadcrumbItem[],
	locale: string,
): WithContext<BreadcrumbList> | null => {
	if (items.length < 2) return null;

	const itemListElement: ListItem[] = [];
	for (let i = 0; i < items.length; i++) {
		const item = items[i];
		const isLast = i === items.length - 1;
		const listItem: ListItem = {
			"@type": "ListItem",
			position: i + 1,
			name: item.label,
		};
		if (!isLast) {
			if (!item.href) return null;
			listItem.item = withDomain(locale, item.href);
		}
		itemListElement.push(listItem);
	}

	return {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement,
	};
};

export const BreadcrumbsSchema = ({ items }: BreadcrumbsSchemaProps) => {
	const { language, country } = useGeo();
	const schema = buildSchema(items, `${language}-${country}`);
	if (!schema) return null;

	return (
		<script
			type="application/ld+json"
			// biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD requires raw script content; values are typed via schema-dts and escaped via safeStringify
			dangerouslySetInnerHTML={{ __html: safeStringify(schema) }}
		/>
	);
};
